import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

const ZipcodeList = () => {
  const [zipcodes, setZipcodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  
  // Truncate dialog and snackbar states
  const [openTruncateDialog, setOpenTruncateDialog] = useState(false);
  const [truncating, setTruncating] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchZipcodes();
  }, []);

  const fetchZipcodes = async () => {
    try {
      setLoading(true);
      setError(null);
      const auth = JSON.parse(localStorage.getItem("auth")) || {};
      const { accessToken } = auth;
      
      const apiUrl = process.env.REACT_APP_BASE_API_URL;
      
      const response = await axios.get(
        `${apiUrl}/wms/api/zipcodes`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true,
          credentials: 'include'
        }
      );
      
      setZipcodes(response.data.data || []);
      setPage(0);
    } catch (error) {
      console.error('Error fetching zipcodes:', error);
      const errorMessage = error.response?.data?.message || 
                         (error.code === 'ERR_NETWORK' ? 'Network error. Please check your connection.' : 'Failed to fetch zipcodes.');
      setError(errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleDownload = () => {
    // Convert zipcodes to CSV format
    const csvContent = [
      // Header row
      ['Pincode', 'Backend'],
      // Data rows
      ...zipcodes.map(zipcode => [
        // Escape commas and quotes in the warehouse name
        zipcode.pincode,
        zipcode.warehouse_name ? `"${zipcode.warehouse_name.replace(/"/g, '""')}"` : '',
      ])
    ].map(row => row.join(',')).join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `zipcodes-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTruncateZipcodes = async () => {
    try {
      setTruncating(true);
      const auth = JSON.parse(localStorage.getItem("auth")) || {};
      const { accessToken } = auth;
      
      const apiUrl = process.env.REACT_APP_BASE_API_URL;
      
      await axios.post(
        `${apiUrl}/wms/api/zipcodes/truncate`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true,
          credentials: 'include'
        }
      );
      
      setSnackbar({
        open: true,
        message: 'Zipcode table has been truncated successfully',
        severity: 'success'
      });
      
      setOpenTruncateDialog(false);
      fetchZipcodes(); // Refresh the list after truncation
    } catch (error) {
      console.error('Error truncating zipcodes:', error);
      const errorMessage = error.response?.data?.message || 
                         (error.code === 'ERR_NETWORK' ? 'Network error. Please check your connection.' : 'Failed to truncate zipcodes.');
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setTruncating(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const filteredZipcodes = zipcodes.filter(zipcode => 
    (zipcode.warehouse_name && zipcode.warehouse_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (zipcode.pincode && zipcode.pincode.includes(searchTerm))
  );

  const displayedZipcodes = filteredZipcodes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading && zipcodes.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 2,
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Zipcode Mappings</Typography>
          
          <Tooltip title="Refresh data">
            <IconButton 
              onClick={fetchZipcodes} 
              disabled={refreshing}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* <Tooltip title="Truncate zipcode table">
            <IconButton 
              onClick={() => setOpenTruncateDialog(true)}
              disabled={refreshing || zipcodes.length === 0}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip> */}
          
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={zipcodes.length === 0}
          >
            Download CSV
          </Button>
        </Box>
      </Box>

      <TextField
        fullWidth
        label="Search by warehouse or pincode"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(0);
        }}
        sx={{ mb: 2 }}
      />

      {error && (
        <Box sx={{ color: 'error.main', p: 2, mb: 2 }}>
          <Typography>{error}</Typography>
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Warehouse</strong></TableCell>
                  <TableCell><strong>Pincode</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedZipcodes.map((zipcode, index) => (
                  <TableRow key={index}>
                    <TableCell>{zipcode.warehouse_name}</TableCell>
                    <TableCell>{zipcode.pincode}</TableCell>
                  </TableRow>
                ))}
                {(filteredZipcodes.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No zipcodes found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredZipcodes.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
          />
        </Paper>
      )}
      
      {/* Truncate Confirmation Dialog */}
      <Dialog
        open={openTruncateDialog}
        onClose={() => !truncating && setOpenTruncateDialog(false)}
      >
        <DialogTitle>Confirm Truncate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to truncate the zipcode table? This will delete ALL zipcode mappings and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenTruncateDialog(false)} 
            disabled={truncating}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleTruncateZipcodes} 
            color="error" 
            disabled={truncating}
            variant="contained"
          >
            {truncating ? 'Truncating...' : 'Truncate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ZipcodeList;
